import { useActiveDataflowId } from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { logger } from "@/utils/debug";
import { Button, Flex, Group, Modal, Select, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useMemo } from "react";
import {
  IconBuildingCommunity,
  IconClock,
  IconGrain,
  IconWorld,
} from "@tabler/icons-react";

export function WorkspaceModalMapVisualization({
  onClose,
  title = "Configure Visualization Mode",
}: {
  onClose: () => void;
  title?: string;
}) {
  const activeDataflowId = useActiveDataflowId();
  const setVisualizationMode = useMapStore(
    (state) => state.setVisualizationMode
  );
  const getVisualizationMode = useMapStore(
    (state) => state.getVisualizationMode
  );
  const visualizationMode = getVisualizationMode(activeDataflowId || "");

  // Build visualization mode dropdown options with icons
  const visualizationOptions = useMemo(() => {
    return [
      {
        group: "Point layers",
        items: [
          {
            value: "points-cluster",
            label: "Clusters",
            icon: IconGrain,
          },
        ],
      },
      {
        group: "Brick layers",
        items: [
          {
            value: "layers-municipalities",
            label: "Municipalities",
            icon: IconBuildingCommunity,
          },
          {
            value: "layers-countries",
            label: "Countries",
            icon: IconWorld,
          },
          {
            value: "layers-timezones",
            label: "Timezones",
            icon: IconClock,
          },
        ],
      },
    ];
  }, []);

  // Create a map of value to icon for easy lookup
  const visualizationIconMap = useMemo(() => {
    const map = new Map();
    visualizationOptions.forEach((group) => {
      group.items.forEach((item) => {
        if (item.icon) {
          map.set(item.value, item.icon);
        }
      });
    });
    return map;
  }, [visualizationOptions]);

  // Get current visualization mode value for dropdown
  const currentVisualizationValue = useMemo(() => {
    if (!visualizationMode) {
      return "points-cluster"; // Default
    }

    if (visualizationMode.mode === "points") {
      return "points-cluster";
    } else if (
      visualizationMode.mode === "layers" &&
      visualizationMode.layerType
    ) {
      return `layers-${visualizationMode.layerType}`;
    }

    return "points-cluster";
  }, [visualizationMode]);

  const form = useForm<{
    visualizationMode: string;
  }>({
    initialValues: {
      visualizationMode: currentVisualizationValue,
    },
  });

  // Reset form values when modal opens or when visualization mode changes
  useEffect(() => {
    if (form.values.visualizationMode !== currentVisualizationValue) {
      form.setValues({
        visualizationMode: currentVisualizationValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVisualizationValue]);

  const handleSubmit = (values: { visualizationMode: string }) => {
    if (!activeDataflowId) {
      logger.error("Cannot save visualization mode: missing activeDataflowId");
      return;
    }

    // Handle visualization mode change
    if (values.visualizationMode) {
      const [mode, subOption] = values.visualizationMode.split("-");

      if (mode === "points") {
        setVisualizationMode(activeDataflowId, "points", null);
      } else if (mode === "layers") {
        const layerType = subOption as
          | "municipalities"
          | "countries"
          | "timezones";
        setVisualizationMode(activeDataflowId, "layers", layerType);
      }
    }

    onClose();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={title}
      size="md"
      closeOnClickOutside={false}
      centered
    >
      <form
        onSubmit={form.onSubmit(handleSubmit, (errors) => {
          logger.error("Form validation errors:", errors);
        })}
      >
        <Stack gap="md">
          <Select
            label="Visualization Mode"
            placeholder="Select visualization mode..."
            data={visualizationOptions}
            {...form.getInputProps("visualizationMode")}
            description="Choose how to display the data on the map"
            renderOption={({ option }) => {
              const IconComponent = visualizationIconMap.get(option.value);
              return (
                <Flex align="center" gap={8}>
                  {IconComponent && <IconComponent size={16} stroke={1.4} />}
                  <Text size="sm">{option.label}</Text>
                </Flex>
              );
            }}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

